import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Package2, Filter, Check, IndianRupee, RotateCcw, History, Building2, TrendingUp, TrendingDown, Plus } from "lucide-react";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const fmt = (n) => (Number(n || 0)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function MaterialLibrary() {
  const { user, hasPermission } = useAuth();
  const canEdit = user?.role === "super_admin" || user?.role === "director"
    || user?.role === "purchase_manager" || user?.role === "accountant"
    || hasPermission("boq", "edit") || hasPermission("inventory", "edit");

  const [modules, setModules] = useState([]);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [module, setModule] = useState("");
  const [category, setCategory] = useState("");
  const [onlyUnpriced, setOnlyUnpriced] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savingCode, setSavingCode] = useState(null);
  const [drafts, setDrafts] = useState({}); // code -> pending price string
  const [vendors, setVendors] = useState([]);        // preferred-supplier lookup
  const [historyFor, setHistoryFor] = useState(null); // item currently shown in price-history modal
  const [addOpen, setAddOpen] = useState(false);
  const emptyMat = { name: "", category: "", subcategory: "", uom: "", brand: "", description: "", per_unit_price: "" };
  const [newMat, setNewMat] = useState(emptyMat);
  const [creating, setCreating] = useState(false);
  const createMaterial = async () => {
    if (!newMat.name.trim()) return toast.error("Material name is required");
    setCreating(true);
    try {
      await api.post("/material-library", { ...newMat, per_unit_price: Number(newMat.per_unit_price || 0) });
      toast.success(`Custom material added · ${newMat.name}`);
      setAddOpen(false); setNewMat(emptyMat);
      loadModules(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add material"); }
    finally { setCreating(false); }
  };

  const loadModules = async () => {
    try {
      const [m, v] = await Promise.all([api.get("/material-library/modules"), api.get("/vendors").catch(() => ({ data: [] }))]);
      setModules(m.data); setVendors(v.data);
    } catch { setModules([]); }
  };
  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (q) p.append("q", q);
      if (module) p.append("module", module);
      if (category) p.append("category", category);
      if (onlyUnpriced) p.append("only_unpriced", "true");
      const { data } = await api.get(`/material-library?${p.toString()}`);
      setItems(data);
    } catch (e) { toast.error("Failed to load library"); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadModules(); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t);   }, [q, module, category, onlyUnpriced]);

  const categories = useMemo(() => {
    const set = new Set(items.map(i => i.category).filter(Boolean));
    return Array.from(set).sort();
  }, [items]);

  const savePrice = async (item) => {
    const raw = drafts[item.item_code];
    if (raw === undefined) return;
    const val = Number(raw);
    if (Number.isNaN(val) || val < 0) return toast.error("Enter a valid non-negative price");
    setSavingCode(item.item_code);
    try {
      const { data } = await api.patch(`/material-library/${item.item_code}`, { per_unit_price: val });
      setItems(list => list.map(i => i.item_code === item.item_code ? data : i));
      const nd = { ...drafts }; delete nd[item.item_code]; setDrafts(nd);
      toast.success(`Price saved · ${item.name}`);
      loadModules();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSavingCode(null); }
  };

  const changeSupplier = async (item, vid) => {
    try {
      const { data } = await api.patch(`/material-library/${item.item_code}`, { preferred_supplier_id: vid || null });
      setItems(list => list.map(i => i.item_code === item.item_code ? data : i));
      toast.success("Supplier updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const stats = useMemo(() => {
    const total = items.length;
    const priced = items.filter(i => Number(i.per_unit_price || 0) > 0).length;
    return { total, priced, unpriced: total - priced };
  }, [items]);

  return (
    <div className="space-y-6" data-testid="material-library-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1 flex items-center gap-2"><Package2 className="w-3.5 h-3.5" /> Master Catalog</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold">Material Library</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">One source of truth for materials across BOQ, Estimation, Procurement, Inventory & Finance. Set the <b>Per Unit Price</b> below — it will be reused everywhere.</p>
        </div>
        <div className="flex flex-col items-end gap-3">
          {canEdit && (
            <Button onClick={() => { setNewMat(emptyMat); setAddOpen(true); }} data-testid="add-custom-material-btn" className="bg-primary text-primary-foreground">
              <Plus className="w-4 h-4 mr-1.5" /> Add Custom Material
            </Button>
          )}
          <div className="flex gap-3 text-xs">
            <div className="rounded-lg border border-border/50 px-3 py-2"><div className="uppercase tracking-widest text-[9px] text-muted-foreground">Total</div><div className="font-display text-lg">{stats.total}</div></div>
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2"><div className="uppercase tracking-widest text-[9px] text-emerald-600">Priced</div><div className="font-display text-lg text-emerald-600">{stats.priced}</div></div>
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2"><div className="uppercase tracking-widest text-[9px] text-amber-600">Unpriced</div><div className="font-display text-lg text-amber-600">{stats.unpriced}</div></div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-2xl bg-card border border-border/60 p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search by name, code, category, spec…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-10" data-testid="lib-search" />
        </div>
        <Select value={module || "__all__"} onValueChange={(v) => { setModule(v === "__all__" ? "" : v); setCategory(""); }}>
          <SelectTrigger className="w-56" data-testid="lib-module-filter"><SelectValue placeholder="All modules" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All modules ({modules.reduce((s, m) => s + m.count, 0)})</SelectItem>
            {modules.map(m => (
              <SelectItem key={m.module_code} value={m.module_code}>{m.module_code} · {m.module_name} ({m.count})</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {categories.length > 0 && (
          <Select value={category || "__all__"} onValueChange={(v) => setCategory(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-48" data-testid="lib-category-filter"><SelectValue placeholder="All categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All categories</SelectItem>
              {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
          <input type="checkbox" checked={onlyUnpriced} onChange={e => setOnlyUnpriced(e.target.checked)} className="rounded border-border accent-primary" data-testid="lib-unpriced-toggle" />
          <span>Unpriced only</span>
        </label>
      </div>

      {/* Table */}
      <div className="rounded-2xl bg-card border border-border/60 overflow-hidden">
        <div className="overflow-auto max-h-[70vh]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted/50 backdrop-blur text-[10px] uppercase tracking-[0.15em] text-muted-foreground z-10">
              <tr>
                <th className="text-left py-2.5 px-3 w-24">Code</th>
                <th className="text-left w-24">Module</th>
                <th className="text-left w-32">Category</th>
                <th className="text-left">Item</th>
                <th className="text-left w-28">UOM</th>
                <th className="text-left w-56">Preferred Supplier</th>
                <th className="text-right w-40">Per Unit Price (₹)</th>
                <th className="w-12 pr-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">Loading…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">No materials match these filters.</td></tr>}
              {items.map(m => {
                const priced = Number(m.per_unit_price || 0) > 0;
                const draft = drafts[m.item_code];
                const dirty = draft !== undefined && Number(draft) !== Number(m.per_unit_price || 0);
                return (
                  <tr key={m.item_code} data-testid={`lib-row-${m.item_code}`} className="border-t border-border/50 hover:bg-muted/20">
                    <td className="py-2 px-3 font-mono text-[11px]">{m.item_code}</td>
                    <td className="text-[11px] text-muted-foreground">{m.module_code}</td>
                    <td className="text-xs">{m.category}</td>
                    <td>
                      <div className="font-medium">{m.name}</div>
                      {m.spec && <div className="text-[10px] text-muted-foreground">{m.spec}</div>}
                    </td>
                    <td className="text-xs text-muted-foreground">{m.uom}</td>
                    <td>
                      <Select value={m.preferred_supplier_id || "__none__"} onValueChange={(v) => canEdit && changeSupplier(m, v === "__none__" ? "" : v)}>
                        <SelectTrigger className="h-7 text-[11px] w-52" data-testid={`lib-supplier-${m.item_code}`} disabled={!canEdit}>
                          <SelectValue placeholder="Unassigned" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Unassigned —</SelectItem>
                          {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td>
                      <div className="flex items-center justify-end gap-2">
                        <div className="relative">
                          <IndianRupee className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                          <input
                            type="number" min="0" step="0.01"
                            disabled={!canEdit}
                            data-testid={`lib-price-${m.item_code}`}
                            value={draft !== undefined ? draft : (m.per_unit_price || "")}
                            placeholder="0.00"
                            onChange={(e) => setDrafts({ ...drafts, [m.item_code]: e.target.value })}
                            onKeyDown={(e) => { if (e.key === "Enter") savePrice(m); }}
                            className={`w-32 h-8 text-sm text-right pl-6 pr-2 rounded border bg-background disabled:opacity-50 ${dirty ? "border-primary" : priced ? "border-border/60" : "border-amber-500/40 bg-amber-500/5"}`}
                          />
                        </div>
                        {dirty && canEdit && (
                          <button onClick={() => savePrice(m)} disabled={savingCode === m.item_code} data-testid={`lib-save-${m.item_code}`} className="p-1 rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                            <Check className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {dirty && (
                          <button onClick={() => { const nd = { ...drafts }; delete nd[m.item_code]; setDrafts(nd); }} className="p-1 rounded hover:bg-muted text-muted-foreground" title="Discard">
                            <RotateCcw className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="pr-3 text-center">
                      <button onClick={() => setHistoryFor(m)} data-testid={`lib-history-${m.item_code}`} className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-primary" title="Price history">
                        <History className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <PriceHistoryDialog material={historyFor} onClose={() => setHistoryFor(null)} />

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg" data-testid="add-material-dialog">
          <DialogHeader>
            <DialogTitle>Add Custom Material</DialogTitle>
            <DialogDescription>Saved to the Material Library and instantly available in BOQ, Estimation, Procurement, Inventory & Finance.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Material Name *</Label><Input data-testid="mat-name" value={newMat.name} onChange={(e) => setNewMat({ ...newMat, name: e.target.value })} /></div>
            <div><Label>Category</Label><Input data-testid="mat-category" value={newMat.category} onChange={(e) => setNewMat({ ...newMat, category: e.target.value })} /></div>
            <div><Label>Subcategory</Label><Input data-testid="mat-subcategory" value={newMat.subcategory} onChange={(e) => setNewMat({ ...newMat, subcategory: e.target.value })} /></div>
            <div><Label>Unit (UOM)</Label><Input data-testid="mat-uom" value={newMat.uom} onChange={(e) => setNewMat({ ...newMat, uom: e.target.value })} placeholder="e.g. Nos, Sqft, Kg" /></div>
            <div><Label>Brand</Label><Input data-testid="mat-brand" value={newMat.brand} onChange={(e) => setNewMat({ ...newMat, brand: e.target.value })} /></div>
            <div className="col-span-2"><Label>Description</Label><Input data-testid="mat-description" value={newMat.description} onChange={(e) => setNewMat({ ...newMat, description: e.target.value })} /></div>
            <div className="col-span-2"><Label>Per Unit Price (₹)</Label><Input data-testid="mat-price" type="number" min="0" step="0.01" value={newMat.per_unit_price} onChange={(e) => setNewMat({ ...newMat, per_unit_price: e.target.value })} /></div>
          </div>
          <Button onClick={createMaterial} disabled={creating} data-testid="save-material-btn" className="bg-primary text-primary-foreground">{creating ? "Adding…" : "Add Material"}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -------------- Price History Dialog --------------
function PriceHistoryDialog({ material, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!material) return;
    setLoading(true);
    api.get(`/material-library/${material.item_code}/history`)
      .then(({ data }) => setRows(data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [material]);

  const first = rows[rows.length - 1];
  const latest = rows[0];
  const netDelta = first && latest ? latest.new_price - first.old_price : 0;
  const netPct = first && first.old_price > 0 ? (netDelta / first.old_price) * 100 : null;

  return (
    <Dialog open={!!material} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="w-4 h-4 text-primary" /> Price History · {material?.name}</DialogTitle>
          <DialogDescription>{material?.item_code} · {material?.module_name} · Current: ₹{fmt(material?.per_unit_price || 0)}</DialogDescription>
        </DialogHeader>

        {rows.length > 0 && (
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div className="rounded-lg border border-border/60 p-3"><div className="uppercase tracking-widest text-[9px] text-muted-foreground">Changes</div><div className="font-display text-2xl mt-1">{rows.length}</div></div>
            <div className="rounded-lg border border-border/60 p-3"><div className="uppercase tracking-widest text-[9px] text-muted-foreground">First recorded</div><div className="font-display text-lg mt-1">₹{fmt(first?.old_price || 0)}</div></div>
            <div className={`rounded-lg border p-3 ${netDelta > 0 ? "border-rose-500/40 bg-rose-500/5" : netDelta < 0 ? "border-emerald-500/40 bg-emerald-500/5" : "border-border/60"}`}>
              <div className="uppercase tracking-widest text-[9px] text-muted-foreground flex items-center gap-1">
                {netDelta > 0 ? <TrendingUp className="w-3 h-3 text-rose-500" /> : netDelta < 0 ? <TrendingDown className="w-3 h-3 text-emerald-500" /> : null}
                Net change
              </div>
              <div className={`font-display text-lg mt-1 ${netDelta > 0 ? "text-rose-500" : netDelta < 0 ? "text-emerald-500" : ""}`}>
                {netDelta >= 0 ? "+" : ""}₹{fmt(Math.abs(netDelta))}
                {netPct !== null && <span className="text-xs ml-1">({netDelta >= 0 ? "+" : ""}{netPct.toFixed(1)}%)</span>}
              </div>
            </div>
          </div>
        )}

        <div className="max-h-[50vh] overflow-auto border border-border/60 rounded-lg">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted/50 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
              <tr>
                <th className="text-left py-2 px-3">Date</th>
                <th className="text-right">Old</th>
                <th className="text-center w-12"></th>
                <th className="text-right">New</th>
                <th className="text-right">Δ</th>
                <th className="text-left pl-3">Changed by</th>
                <th className="text-left">Note</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">No price changes recorded yet.</td></tr>}
              {rows.map(h => (
                <tr key={h.id} data-testid={`hist-row-${h.id}`} className="border-t border-border/50">
                  <td className="py-2 px-3 text-xs">{new Date(h.changed_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</td>
                  <td className="text-right text-muted-foreground line-through">₹{fmt(h.old_price)}</td>
                  <td className="text-center text-muted-foreground">→</td>
                  <td className="text-right font-medium">₹{fmt(h.new_price)}</td>
                  <td className={`text-right ${h.delta > 0 ? "text-rose-500" : "text-emerald-500"}`}>
                    {h.delta >= 0 ? "+" : ""}₹{fmt(Math.abs(h.delta))}
                    {h.percent_change !== null && h.percent_change !== undefined && <div className="text-[10px]">({h.delta >= 0 ? "+" : ""}{h.percent_change}%)</div>}
                  </td>
                  <td className="text-xs pl-3">{h.changed_by_name || "—"}</td>
                  <td className="text-xs text-muted-foreground">{h.note || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
